import { yupResolver } from "@hookform/resolvers/yup";
import {Alert, Box, Typography} from "@mui/material";
import { useEffect, useRef, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import EditIcon from "@mui/icons-material/Edit";
import {
    addAndEditStoreSchema,
    validateCnpj,
    validatePhoneNumber,
} from "../storeValidationSchemas";
import ComercialDrawer from "../../../../../components/pet-beauty/CormercialDrawer";
import { PrimaryButton } from "../../../../../components/general/buttons/PrimaryButton";
import { SecondaryButton } from "../../../../../components/general/buttons/SecondaryButton";
import storeApi from "../../../../../services/api/Store";
import { IStore } from "../../../../../services/api/Store/types";
import { sharedStyles } from "../../../../../styles/shared";
import { getLocalStorageItem } from "../../../../../hooks/localStorageHooks";
import { LeftMarginBackButton } from "@/components/general/buttons/BackPageButton";
import { DEFAULT_COORDINATES } from "../AddStore";
import { StoreDataForm } from "@/pages/petAndBeauty/Comercial/StoresPages/StoreForm/components/StoreDataForm";
import { StoreWorkingScale } from "@/pages/petAndBeauty/Comercial/StoresPages/StoreForm/components/StoreWorkingScale";
import { BankingDataForm } from "@/pages/petAndBeauty/Comercial/StoresPages/StoreForm/components/BankingDataForm";
import { AddressDataForm } from "@/pages/petAndBeauty/Comercial/StoresPages/StoreForm/components/AddressDataForm";
import {
    GenericImageSelector,
    ProcessedImage
} from "@/components/general/Images/GenericImageSelector";
import ImageGalleryManagerApi from "@/components/general/Images/ImageGalleryManagerApi";

const EditStore = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const selectedStore: IStore = getLocalStorageItem("selectedStore")!;

    const refs = {
        storeName: useRef<HTMLInputElement>(null),
        cnpj: useRef<HTMLInputElement>(null),
        phone: useRef<HTMLInputElement>(null),
        email: useRef<HTMLInputElement>(null),
        description: useRef<HTMLInputElement>(null),
        street: useRef<HTMLInputElement>(null),
        number: useRef<HTMLInputElement>(null),
        district: useRef<HTMLInputElement>(null),
        city: useRef<HTMLInputElement>(null),
        state: useRef<HTMLInputElement>(null),
        bank: useRef<HTMLInputElement>(null),
        issuer: useRef<HTMLInputElement>(null),
        issuerDigit: useRef<HTMLInputElement>(null),
        accountNumber: useRef<HTMLInputElement>(null),
        bankDigit: useRef<HTMLInputElement>(null),
        pixKey: useRef<HTMLInputElement>(null),
    };

    const [cnpj, setCnpj] = useState<string>(selectedStore?.CNPJ || "");
    const [phone, setPhone] = useState<string>(selectedStore?.phone || "");

    const [gatewayId, setGatewayId] = useState(selectedStore?.gatewayId || "");
    const [accountType, setAccountType] = useState(selectedStore?.account?.accountType || "CHECKING_ACCOUNT");
    const [pixKeyType, setPixKeyType] = useState("CPF");

    const [cep, setCep] = useState<string>(selectedStore?.CEP || "");
    const [coordinates, setCoordinates] = useState<[number, number]>(
        selectedStore?.lat && selectedStore?.lng ? [selectedStore.lat, selectedStore.lng] : DEFAULT_COORDINATES
    );

    const [selectedImages, setSelectedImages] = useState<ProcessedImage[]>([]);

    const [intervalMinutes, setIntervalMinutes] = useState<number>(60);
    const [workingHours, setWorkingHours] = useState<any>({
        timeMonday: selectedStore?.timeMonday || [],
        timeTuesday: selectedStore?.timeTuesday || [],
        timeWednesday: selectedStore?.timeWednesday || [],
        timeThursday: selectedStore?.timeThursday || [],
        timeFriday: selectedStore?.timeFriday || [],
        timeSaturday: selectedStore?.timeSaturday || [],
        timeSunday: selectedStore?.timeSunday || [],
    });

    const [buttonsDisabled, setButtonsDisabled] = useState<boolean>(false);
    const [submittedOnce, setSubmittedOnce] = useState<boolean>(false);
    const [generalErrorMessage, setGeneralErrorMessage] = useState("");
    const [firstTimeFetched, setFirstTimeFetched] = useState(false);

    const {
        register,
        handleSubmit: onSubmit,
        formState: { errors },
        getValues,
    } = useForm({
        resolver: yupResolver(addAndEditStoreSchema),
        defaultValues: {
            storeName: selectedStore?.name || "",
            email: selectedStore?.email || "",
        }
    });

    useEffect(() => {
        if (selectedStore?.CEP !== cep && !firstTimeFetched) {
            setFirstTimeFetched(true);
        }
    }, [cep, selectedStore?.CEP, firstTimeFetched]);

    const handleImageChange = useCallback((images: ProcessedImage[]) => {
        setSelectedImages(images);
    }, []);

    const handleUpdateStore = async () => {
        setGeneralErrorMessage("");
        if (!submittedOnce) setSubmittedOnce(true);

        const isCnpjValid = validateCnpj(cnpj);
        const isPhoneValid = validatePhoneNumber(phone);

        if (isCnpjValid && isPhoneValid) {
            setButtonsDisabled(true);

            try {
                // Resgatando valores dos inputs de forma segura
                const formValues = getValues();
                const storeNameValue = refs.storeName?.current?.value || formValues.storeName || selectedStore.name;
                const emailValue = refs.email?.current?.value || formValues.email || selectedStore.email;
                const descriptionValue = refs.description?.current?.value !== undefined ? refs.description.current.value : selectedStore.description;

                const streetValue = refs.street?.current?.value !== undefined ? refs.street.current.value : selectedStore.logradouro;
                const numberValue = refs.number?.current?.value !== undefined ? refs.number.current.value : selectedStore.number;
                const districtValue = refs.district?.current?.value !== undefined ? refs.district.current.value : selectedStore.district;
                const cityValue = refs.city?.current?.value !== undefined ? refs.city.current.value : selectedStore.city;
                const stateValue = refs.state?.current?.value !== undefined ? refs.state.current.value : selectedStore.state;

        const storeData = {
            name: storeNameValue,
            email: emailValue,
            CNPJ: cnpj,
            phone: phone,
            description: descriptionValue || "",
            logradouro: streetValue || "",
            number: numberValue || "",
            district: districtValue || "",
            city: cityValue || "",
            state: stateValue || "",
            CEP: cep,
            country: "Brazil",
            lat: coordinates[0],
            lng: coordinates[1],
            listTags: selectedStore?.listTags || "",
            timeMonday: workingHours.timeMonday,
            timeTuesday: workingHours.timeTuesday,
            timeWednesday: workingHours.timeWednesday,
            timeThursday: workingHours.timeThursday,
            timeFriday: workingHours.timeFriday,
            timeSaturday: workingHours.timeSaturday,
            timeSunday: workingHours.timeSunday,
        };

                const accountData = {
                    bank: refs.bank?.current?.value || selectedStore?.account?.bank || "",
                    issuer: refs.issuer?.current?.value || selectedStore?.account?.issuer || "",
                    issuerDigit: refs.issuerDigit?.current?.value || selectedStore?.account?.issuerDigit || "",
                    number: refs.accountNumber?.current?.value || selectedStore?.account?.number || "",
                    bankDigit: refs.bankDigit?.current?.value || selectedStore?.account?.bankDigit || "",
                    accountType: accountType,
                };

                const formData = new FormData();
                formData.append("store", JSON.stringify(storeData));
                formData.append("account", JSON.stringify(accountData));
                formData.append("gatewayId", gatewayId);

                selectedImages.forEach((img, index) => {
                    formData.append("images", img.resizedFull, `image_${index}.jpg`);
                });

                const updatedStoreResponse = await storeApi.updateStore(
                    selectedStore.id,
                    formData
                );

                window.alert(
                    t("storePage.management.edit.successMessage", { name: updatedStoreResponse.name })
                );
                navigate("/stores");
            } catch (error: any) {
                setButtonsDisabled(false);
                console.warn("Error updating the store: ", error);
                setGeneralErrorMessage(
                    error.response?.data?.message || t("storePage.management.edit.errorMessage")
                );
            }
    } else {
    if (!isCnpjValid) refs.cnpj.current?.focus();
        else if (!isPhoneValid) refs.phone.current?.focus();
    }
};

    const renderEditStorePageContent = (
        <>
            <LeftMarginBackButton />
            <form onSubmit={onSubmit(handleUpdateStore)} style={sharedStyles.form}>

        <StoreDataForm
                    register={register}
                    errors={errors}
                    refs={refs}
                    cnpj={cnpj}
                    setCnpj={setCnpj}
                    phone={phone}
                    setPhone={setPhone}
                    submittedOnce={submittedOnce}
                    defaultValueName={selectedStore?.name}
                    defaultValueEmail={selectedStore?.email}
                    defaultValueDescription={selectedStore?.description}
                />

        <AddressDataForm
                    cep={cep}
                    setCep={setCep}
                    refs={refs}
                    coordinates={coordinates}
                    setCoordinates={setCoordinates}
                    defaultValueStreet={selectedStore?.logradouro}
                    defaultValueNumber={selectedStore?.number}
                    defaultValueDistrict={selectedStore?.district}
                    defaultValueCity={selectedStore?.city}
                    defaultValueState={selectedStore?.state}
                />

                <BankingDataForm
                    refs={refs}
                    accountType={accountType}
                    setAccountType={setAccountType}
                    pixKeyType={pixKeyType}
                    setPixKeyType={setPixKeyType}
                    gatewayId={gatewayId}
                    setGatewayId={setGatewayId}
                    defaultValueGatewayId={selectedStore?.gatewayId}
                    defaultValueBank={selectedStore?.account?.bank}
                    defaultValueIssuer={selectedStore?.account?.issuer}
                    defaultValueIssuerDigit={selectedStore?.account?.issuerDigit}
                    defaultValueAccountNumber={selectedStore?.account?.number}
                    defaultValueBankDigit={selectedStore?.account?.bankDigit}
                />

                {selectedStore?.id && (
                <Box sx={{ width: "100%", maxWidth: 800, mx: "auto", mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: "text.primary" }}>
                    Gerenciar Imagens Salvas (Clique na lixeira para remover permanentemente)
                </Typography>
                <ImageGalleryManagerApi images={selectedStore.images ?? []} />
                </Box>
            )}

                <GenericImageSelector
                    fullWidth={800}
                    fullHeight={600}
                    dropzoneText="Selecione ou arraste as fotos da loja"
                    onChangeImages={handleImageChange}
                />

                <StoreWorkingScale
                    intervalMinutes={intervalMinutes}
                    setIntervalMinutes={setIntervalMinutes}
                    workingHours={workingHours}
                    setWorkingHours={setWorkingHours}
                />

        {generalErrorMessage !== "" && (
        <Alert severity="error">{generalErrorMessage}</Alert>
        )}

        <Box sx={sharedStyles.dividedFormContainer}>
          <PrimaryButton
            label={t("storePage.management.edit.buttons.confirm")}
            isDisabled={buttonsDisabled}
            icon={<EditIcon />}
            isLoading={buttonsDisabled}
          />
          <SecondaryButton
            label={t("storePage.management.edit.buttons.cancel")}
            onClickAction={() => navigate("/stores")}
            isDisabled={buttonsDisabled}
          />
        </Box>
      </form>
    </>
  );

  return (
    <Box sx={sharedStyles.sharedMainContainer}>
      <ComercialDrawer content={renderEditStorePageContent} />
    </Box>
  );
};

export default EditStore;
